import { Badge } from "@/components/ui/baseUi/badge"
import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { word } from "@/modules/core/utils/i18n"
import { cn } from "@/modules/lib/utils"
import {
  TemplateModalView,
  type TemplateModalViewRef,
} from "@/modules/template/components/sidebarView/templateModalView"
import type { TemplateEntity } from "@/modules/template/entities/templateEntity"
import { useTemplateSessionViewWithTemplateSidebar } from "@/modules/template/hooks/useTemplateSidebar"
import {
  useTemplateImportActions,
  useTemplateImportSelectedTemplate,
} from "@/modules/template/stores/useTemplateImportStore"
import { TemplateType } from "@/modules/template/types/templateType"
import { CalendarIcon, CheckIcon } from "lucide-react"
import { useRef } from "react"

type TemplateListItemProps = {
  template: TemplateEntity
  className?: string
} & React.ComponentProps<"div">

export function TemplateListItem({ template, className, ...props }: TemplateListItemProps) {
  const selectedTemplate = useTemplateImportSelectedTemplate()
  const isSelectedTemplate = selectedTemplate?.id === template.id
  const { setSelectedTemplate, clearSelectedTemplate } = useTemplateImportActions()
  const { navigateToTemplateSessionViewWithTemplate } = useTemplateSessionViewWithTemplateSidebar()
  const templateModalViewRef = useRef<TemplateModalViewRef>(null)

  const onPreviewTemplate = () => {
    switch (template.templateType) {
      case TemplateType.SESSION:
        navigateToTemplateSessionViewWithTemplate({ templateId: template.id })
        break
      case TemplateType.WEEK:
        templateModalViewRef.current?.open()
        break
      case TemplateType.EXERCISE:
        templateModalViewRef.current?.open()
        break
      default: {
        const exhaustiveCheck: never = template.templateType
        throw new Error(`Invalid template type ${String(exhaustiveCheck)}`)
      }
    }
  }

  return (
    <>
      <div
        className={cn(
          "flex min-h-18 w-full cursor-pointer transition-all duration-350 ease-in-out hover:bg-interactive-background-neutral-highlighted",
          isSelectedTemplate && "bg-feedback-positive-subtle",
          className,
        )}
        onClick={() => {
          if (isSelectedTemplate) {
            clearSelectedTemplate()
          } else {
            setSelectedTemplate(template)
          }
        }}
        {...props}
      >
        <div className="mr-4 ml-6 flex grow flex-row items-center gap-3 border-b border-surface-border-gray-subtle py-3">
          <div className="flex grow flex-col">
            <Text fontStyle="LL" className={cn(isSelectedTemplate && "text-feedback-positive-text-subtle")}>
              {template.name}
            </Text>
            <Text fontStyle="LXS" className="line-clamp-1">
              {template.description}
            </Text>
            {template.templateCategory && (
              <Badge variant="neutralSubtle" size="sm">
                {template.templateCategory.name}
              </Badge>
            )}
          </div>
          <Button
            variant={isSelectedTemplate ? "secondarySecondary" : "secondary"}
            size="xs"
            className="transition-all duration-300 ease-in-out"
            onClick={(e) => {
              e.stopPropagation()
              onPreviewTemplate()
            }}
          >
            <CalendarIcon />
            {word("global.preview")}
          </Button>
          {isSelectedTemplate && <CheckIcon color="currentColor" />}
        </div>
      </div>
      {(template.templateType === TemplateType.EXERCISE || template.templateType === TemplateType.WEEK) && (
        <TemplateModalView templateId={template.id} ref={templateModalViewRef} />
      )}
    </>
  )
}
